import React, { useCallback, Dispatch, SetStateAction, RefObject } from "react";
import { ProductResponse } from "@/app/api/types/product";
import { UUID } from "crypto";
import {
  focusNextTabCell,
  focusPrevTabCell,
  focusEnterTarget,
} from "../../utils/tabNavigation";
import { PRODUCT_COMBO_FILTER_IN_STOCK_KEY } from "./useProductSelectState";
import {
  resolveInitialStockFilter,
  type StockFilterMode,
} from "./stockFilterMode";

interface UseProductSelectKeyboardParams {
  isOpen: boolean;
  disabled: boolean;
  isLastRow: boolean;
  selectedDropdownIndex: number;
  previousSkuValue: string;
  productsToRender: ProductResponse[];
  /** True while rendered results are stale vs. the live search term. */
  isSearchPending: boolean;
  stockFilterMode: StockFilterMode;
  // Setters
  setIsOpen: (v: boolean) => void;
  setSearchTerm: (v: string) => void;
  setOpenedByButton: (v: boolean) => void;
  setSelectedDropdownIndex: (idx: number) => void;
  setIsManuallyNavigating: (v: boolean) => void;
  setFilterInStock: Dispatch<SetStateAction<boolean>>;
  setFilterPriced: Dispatch<SetStateAction<boolean>>;
  setSkuInput: (v: string) => void;
  // Refs
  buttonRef: RefObject<HTMLButtonElement | null>;
  dropdownRef: RefObject<HTMLDivElement | null>;
  searchInputRef: RefObject<HTMLInputElement | null>;
  // Callbacks
  handleProductSelect: (
    product: ProductResponse,
    subproductId?: UUID | null,
    options?: { skipAutoAdvance?: boolean },
  ) => void;
  handleToggle: () => void;
  updateDropdownPosition: () => void;
}

export function useProductSelectKeyboard(
  params: UseProductSelectKeyboardParams,
) {
  const {
    isOpen,
    disabled,
    isLastRow,
    selectedDropdownIndex,
    previousSkuValue,
    productsToRender,
    isSearchPending,
    stockFilterMode,
    setIsOpen,
    setSearchTerm,
    setOpenedByButton,
    setSelectedDropdownIndex,
    setIsManuallyNavigating,
    setFilterInStock,
    setFilterPriced,
    setSkuInput,
    buttonRef,
    dropdownRef,
    searchInputRef,
    handleProductSelect,
    handleToggle,
    updateDropdownPosition,
  } = params;

  const scrollSelectedItemIntoView = useCallback(
    (index: number) => {
      const dropdownElement =
        dropdownRef.current?.querySelector(".overflow-y-auto");
      if (!dropdownElement) return;

      const items = dropdownElement.querySelectorAll("[data-dropdown-item]");
      const selectedItem = items[index] as HTMLElement;
      if (!selectedItem) return;

      const containerRect = dropdownElement.getBoundingClientRect();
      const itemRect = selectedItem.getBoundingClientRect();

      const isAbove = itemRect.top < containerRect.top;
      const isBelow = itemRect.bottom > containerRect.bottom;

      if (isAbove || isBelow) {
        selectedItem.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    },
    [dropdownRef],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();
        // Search still settling → the rows on screen are STALE. Swallow the
        // key entirely (no select, no row-step) so "Enter to confirm the
        // search" can't commit a result the user never saw.
        if (isOpen && isSearchPending) return;
        const didSelect = isOpen && productsToRender.length > 0;
        if (didSelect) {
          const product =
            selectedDropdownIndex >= 0 &&
            productsToRender[selectedDropdownIndex]
              ? productsToRender[selectedDropdownIndex]
              : productsToRender[0];
          if (isLastRow) {
            handleProductSelect(product);
            return;
          }
          handleProductSelect(product, null, { skipAutoAdvance: true });
          setIsOpen(false);
        }
        // Enter → step down to the next row's SKU (spreadsheet-style);
        // Shift+Enter → jump to the new-product row. Matches the shared
        // cell-navigation contract used by Price/Quantity/Weight cells.
        focusEnterTarget(event.currentTarget, event.shiftKey);
      } else if (event.key === "Tab") {
        event.preventDefault();
        const didSelect = isOpen && productsToRender.length > 0;
        if (didSelect) {
          const product =
            selectedDropdownIndex >= 0 &&
            productsToRender[selectedDropdownIndex]
              ? productsToRender[selectedDropdownIndex]
              : productsToRender[0];
          if (isLastRow) {
            handleProductSelect(product);
            return;
          }
          handleProductSelect(product, null, { skipAutoAdvance: true });
          setIsOpen(false);
        }
        // Tab → navigate to next/prev cell
        if (event.shiftKey) {
          focusPrevTabCell(event.currentTarget);
        } else if (didSelect && buttonRef.current) {
          focusNextTabCell(buttonRef.current);
        } else {
          focusNextTabCell(event.currentTarget);
        }
      } else if (event.key === "ArrowDown") {
        if (isOpen && productsToRender.length > 0) {
          event.preventDefault();
          setIsManuallyNavigating(true);
          const nextIndex =
            (selectedDropdownIndex + 1) % productsToRender.length;
          setSelectedDropdownIndex(nextIndex);
          requestAnimationFrame(() => scrollSelectedItemIntoView(nextIndex));
        }
      } else if (event.key === "ArrowUp") {
        if (isOpen && productsToRender.length > 0) {
          event.preventDefault();
          setIsManuallyNavigating(true);
          const prevIndex =
            selectedDropdownIndex <= 0
              ? productsToRender.length - 1
              : selectedDropdownIndex - 1;
          setSelectedDropdownIndex(prevIndex);
          requestAnimationFrame(() => scrollSelectedItemIntoView(prevIndex));
        }
      } else if (event.key === "S" && event.shiftKey && isOpen) {
        event.preventDefault();
        setFilterInStock((v) => {
          const next = !v;
          localStorage.setItem(PRODUCT_COMBO_FILTER_IN_STOCK_KEY, String(next));
          return next;
        });
      } else if (event.key === "P" && event.shiftKey && isOpen) {
        event.preventDefault();
        const newVal =
          localStorage.getItem("productCombo_filterPriced") !== "true";
        setFilterPriced(newVal);
        localStorage.setItem("productCombo_filterPriced", String(newVal));
      } else if (event.key === "Escape") {
        setIsOpen(false);
        setSelectedDropdownIndex(-1);
        setSkuInput(previousSkuValue);
      }
    },
    [
      isOpen,
      isSearchPending,
      productsToRender,
      selectedDropdownIndex,
      isLastRow,
      handleProductSelect,
      scrollSelectedItemIntoView,
      previousSkuValue,
      buttonRef,
      setIsOpen,
      setSelectedDropdownIndex,
      setIsManuallyNavigating,
      setSkuInput,
      setFilterInStock,
      setFilterPriced,
    ],
  );

  const handleButtonKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (disabled) return;

      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        // Stale results while a search settles — see handleKeyDown's gate.
        if (isOpen && isSearchPending) return;
        if (isOpen) {
          if (
            selectedDropdownIndex >= 0 &&
            productsToRender[selectedDropdownIndex]
          ) {
            if (isLastRow) {
              handleProductSelect(productsToRender[selectedDropdownIndex]);
              return;
            }
            handleProductSelect(productsToRender[selectedDropdownIndex], null, {
              skipAutoAdvance: true,
            });
          }
          setIsOpen(false);
          // Enter → step down to the next row's SKU (spreadsheet-style);
          // Shift+Enter → jump to the new-product row.
          focusEnterTarget(event.currentTarget, event.shiftKey);
        } else {
          handleToggle();
        }
        return;
      }

      if (event.key === "Tab") {
        if (isOpen) {
          if (
            selectedDropdownIndex >= 0 &&
            productsToRender[selectedDropdownIndex]
          ) {
            if (isLastRow) {
              handleProductSelect(productsToRender[selectedDropdownIndex]);
              return;
            }
            handleProductSelect(productsToRender[selectedDropdownIndex], null, {
              skipAutoAdvance: true,
            });
          }
          setIsOpen(false);
        }
        event.preventDefault();
        if (event.shiftKey) {
          focusPrevTabCell(event.currentTarget);
        } else {
          focusNextTabCell(event.currentTarget);
        }
        return;
      }

      if (event.key === "ArrowDown") {
        event.preventDefault();
        if (!isOpen) {
          updateDropdownPosition();
          setFilterInStock(
            resolveInitialStockFilter(
              stockFilterMode,
              localStorage.getItem(PRODUCT_COMBO_FILTER_IN_STOCK_KEY) !==
                "false",
            ),
          );
          setFilterPriced(
            localStorage.getItem("productCombo_filterPriced") === "true",
          );
          setIsOpen(true);
          setOpenedByButton(true);
          setSelectedDropdownIndex(0);
        } else {
          setIsManuallyNavigating(true);
          const nextIndex =
            (selectedDropdownIndex + 1) % productsToRender.length;
          setSelectedDropdownIndex(nextIndex);
          requestAnimationFrame(() => scrollSelectedItemIntoView(nextIndex));
        }
        return;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        if (!isOpen) {
          updateDropdownPosition();
          setFilterInStock(
            resolveInitialStockFilter(
              stockFilterMode,
              localStorage.getItem(PRODUCT_COMBO_FILTER_IN_STOCK_KEY) !==
                "false",
            ),
          );
          setFilterPriced(
            localStorage.getItem("productCombo_filterPriced") === "true",
          );
          setIsOpen(true);
          setOpenedByButton(true);
          setSelectedDropdownIndex(productsToRender.length - 1);
        } else {
          setIsManuallyNavigating(true);
          const prevIndex =
            selectedDropdownIndex <= 0
              ? productsToRender.length - 1
              : selectedDropdownIndex - 1;
          setSelectedDropdownIndex(prevIndex);
          requestAnimationFrame(() => scrollSelectedItemIntoView(prevIndex));
        }
        return;
      }

      if (event.key === "Escape") {
        setIsOpen(false);
        setSelectedDropdownIndex(-1);
        return;
      }

      if (event.key === "S" && event.shiftKey && isOpen) {
        event.preventDefault();
        setFilterInStock((v) => {
          const next = !v;
          localStorage.setItem(PRODUCT_COMBO_FILTER_IN_STOCK_KEY, String(next));
          return next;
        });
        return;
      }

      if (event.key === "P" && event.shiftKey && isOpen) {
        event.preventDefault();
        const newVal =
          localStorage.getItem("productCombo_filterPriced") !== "true";
        setFilterPriced(newVal);
        localStorage.setItem("productCombo_filterPriced", String(newVal));
        return;
      }

      if (event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();
        updateDropdownPosition();
        setFilterInStock(
          resolveInitialStockFilter(
            stockFilterMode,
            localStorage.getItem(PRODUCT_COMBO_FILTER_IN_STOCK_KEY) !== "false",
          ),
        );
        setFilterPriced(
          localStorage.getItem("productCombo_filterPriced") === "true",
        );
        setIsOpen(true);
        setOpenedByButton(true);
        setSearchTerm(event.key);
        setIsManuallyNavigating(false);
        setSelectedDropdownIndex(0);
        requestAnimationFrame(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus();
            searchInputRef.current.setSelectionRange(1, 1);
          }
        });
      }
    },
    [
      disabled,
      isOpen,
      isSearchPending,
      selectedDropdownIndex,
      productsToRender,
      isLastRow,
      handleProductSelect,
      handleToggle,
      updateDropdownPosition,
      scrollSelectedItemIntoView,
      stockFilterMode,
      setIsOpen,
      setSearchTerm,
      setOpenedByButton,
      setSelectedDropdownIndex,
      setIsManuallyNavigating,
      setFilterInStock,
      setFilterPriced,
      searchInputRef,
    ],
  );

  return {
    handleKeyDown,
    handleButtonKeyDown,
    scrollSelectedItemIntoView,
  };
}
